import React, { useState, useEffect, useMemo } from 'react';
import { Modal } from './Modal';
import { Button } from './Button';
import { ChevronRight, ChevronLeft, Check } from 'lucide-react';
// MIGRATION FIREBASE: Utilise Firebase direct au lieu de l'API Python
import { productsService, categoriesService, promotionsService } from '../services/firebaseService';
import { useToast } from './Toast';

export const PromotionWizard = ({ isOpen, onClose, promotion, onSuccess }) => {
  const toast = useToast();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [productSearch, setProductSearch] = useState('');
  const [showTooltip, setShowTooltip] = useState(null);
  const [errors, setErrors] = useState({});
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    type: 'percent_item',
    discount_type: 'percentage',
    discount_value: 10,
    eligible_products: [],
    eligible_categories: [],
    all_products: false,
    min_cart_amount: null,
    // BOGO fields
    bogo_buy_quantity: 1,
    bogo_get_quantity: 1,
    bogo_cheapest_free: false,
    bogo_max_per_order: null,  // Limite max d'offerts (null = illimité)
    bogo_limit_type: 'per_order',  // 'per_order' ou 'per_day'
    // Conditional discount fields
    conditional_quantity: 2,
    conditional_discount_percent: 50,
    // Dates
    start_date: new Date().toISOString().split('T')[0],
    end_date: new Date(Date.now() + 30*24*60*60*1000).toISOString().split('T')[0],
    start_time: null,
    end_time: null,
    days_active: [],
    limit_per_customer: null,
    limit_total: null,
    priority: 0,
    stackable: false,
    // Display
    badge_text: '',
    badge_color: '#FF6B35',
    is_visible_in_app: true,
    // Code promo
    promo_code: null,
    code_required: false,
    // Build 318: réservé à la première commande (nouveaux clients)
    first_order_only: false,
    status: 'active'
  });

  useEffect(() => {
    if (isOpen) {
      loadData();
      if (promotion) {
        // Mapper les champs de la base vers les champs du formulaire
        const mappedData = {
          ...formData,
          ...promotion,
          // Mapper applies_to vers all_products
          all_products: promotion.applies_to === 'all',
          // Mapper product_ids vers eligible_products
          eligible_products: promotion.product_ids || [],
          // Mapper category_ids vers eligible_categories
          eligible_categories: promotion.category_ids || [],
          // Mapper code (backend) vers promo_code (formulaire)
          promo_code: promotion.code || '',
          // Mapper requires_code (backend) vers code_required (formulaire)
          code_required: promotion.requires_code || false,
          // Mapper first_order_only (règle 1ère commande)
          first_order_only: promotion.first_order_only || promotion.type === 'new_customer' || false,
          // Mapper les limites (convention canonique + anciens champs)
          limit_per_customer: promotion.max_uses_per_customer ?? promotion.per_customer_limit ?? null,
          limit_total: promotion.max_uses ?? promotion.usage_limit ?? null,
          // Mapper happy_hour_start/end vers start_time/end_time
          start_time: promotion.happy_hour_start || null,
          end_time: promotion.happy_hour_end || null,
          // Mapper min_order_amount vers min_cart_amount
          min_cart_amount: promotion.min_order_amount || null,
          // Mapper max_per_order vers bogo_max_per_order
          bogo_max_per_order: promotion.max_per_order || null,
          bogo_limit_type: promotion.bogo_limit_type || 'per_order',
        };
        setFormData(mappedData);
      } else {
        // Reset du formulaire pour une nouvelle promo
        setFormData({
          name: '',
          description: '',
          type: 'percent_item',
          discount_type: 'percentage',
          discount_value: 10,
          eligible_products: [],
          eligible_categories: [],
          all_products: false,
          min_cart_amount: null,
          bogo_buy_quantity: 1,
          bogo_get_quantity: 1,
          bogo_cheapest_free: false,
          bogo_max_per_order: null,
          bogo_limit_type: 'per_order',
          conditional_quantity: 2,
          conditional_discount_percent: 50,
          start_date: new Date().toISOString().split('T')[0],
          end_date: new Date(Date.now() + 30*24*60*60*1000).toISOString().split('T')[0],
          start_time: null,
          end_time: null,
          days_active: [],
          limit_per_customer: null,
          limit_total: null,
          priority: 0,
          stackable: false,
          badge_text: '',
          badge_color: '#FF6B35',
          is_visible_in_app: true,
          promo_code: null,
          code_required: false,
          first_order_only: false,
          status: 'active'
        });
      }
      setStep(1);
      setErrors({});
    }
  }, [isOpen, promotion]);

  const loadData = async () => {
    try {
      // FIREBASE DIRECT
      const [productsRes, categoriesRes] = await Promise.all([
        productsService.getAll(),
        categoriesService.getAll()
      ]);
      setProducts(productsRes.products || []);
      setCategories(categoriesRes.categories || []);
    } catch (error) {
      console.error('Error loading data:', error);
    }
  };

  // ============================================
  // VALIDATION STRICTE
  // ============================================

  const validateStep1 = () => {
    const newErrors = {};
    const needsProductSelection = ['percent_item', 'fixed_item', 'bogo', 'conditional_discount'].includes(formData.type);
    const needsCategorySelection = ['percent_category', 'fixed_category'].includes(formData.type);
    const needsDiscountValue = !['bogo', 'shipping_free'].includes(formData.type);

    // Nom obligatoire
    if (!formData.name || formData.name.trim() === '') {
      newErrors.name = 'Le nom de la promo est requis';
    }

    // Type obligatoire (toujours défini par défaut, mais on vérifie quand même)
    if (!formData.type) {
      newErrors.type = 'Le type de promo est requis';
    }

    // Valeur de réduction obligatoire (sauf BOGO et livraison gratuite)
    if (needsDiscountValue) {
      if (!formData.discount_value || formData.discount_value <= 0) {
        newErrors.discount_value = 'La valeur de réduction est requise (> 0)';
      }
      // Vérifier que le pourcentage est entre 1 et 100
      if (formData.discount_type === 'percentage' && formData.discount_value > 100) {
        newErrors.discount_value = 'Le pourcentage ne peut pas dépasser 100%';
      }
    }

    // Produits obligatoires pour certains types
    if (needsProductSelection && !formData.all_products && formData.eligible_products.length === 0) {
      newErrors.eligible_products = 'Sélectionnez au moins 1 produit ou cochez "Toute la carte"';
    }

    // Catégories obligatoires pour certains types
    if (needsCategorySelection && formData.eligible_categories.length === 0) {
      newErrors.eligible_categories = 'Sélectionnez au moins 1 catégorie';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep2 = () => {
    const newErrors = {};

    // Dates obligatoires
    if (!formData.start_date) {
      newErrors.start_date = 'La date de début est requise';
    }
    if (!formData.end_date) {
      newErrors.end_date = 'La date de fin est requise';
    }

    // Date fin > date début
    if (formData.start_date && formData.end_date) {
      const startDate = new Date(formData.start_date);
      const endDate = new Date(formData.end_date);
      if (endDate < startDate) {
        newErrors.end_date = 'La date de fin doit être après la date de début';
      }
    }

    // Code promo obligatoire si type promo_code
    if (formData.type === 'promo_code') {
      if (!formData.promo_code || formData.promo_code.trim() === '') {
        newErrors.promo_code = 'Le code promo est requis';
      } else if (formData.promo_code.includes(' ')) {
        newErrors.promo_code = 'Le code ne doit pas contenir d\'espaces';
      }
    }

    // Seuil obligatoire si type threshold
    if (formData.type === 'threshold') {
      if (!formData.min_cart_amount || formData.min_cart_amount <= 0) {
        newErrors.min_cart_amount = 'Le montant minimum est requis';
      }
    }

    // Horaires obligatoires pour happy_hour
    if (formData.type === 'happy_hour') {
      if (!formData.start_time) {
        newErrors.start_time = 'L\'heure de début est requise';
      }
      if (!formData.end_time) {
        newErrors.end_time = 'L\'heure de fin est requise';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateAll = () => {
    const step1Valid = validateStep1();
    const step2Valid = validateStep2();
    return step1Valid && step2Valid;
  };

  const handleNextStep = () => {
    setErrors({}); // Reset errors
    if (step === 1) {
      if (validateStep1()) {
        setStep(2);
      } else {
        toast.error('Champs manquants', 'Veuillez remplir tous les champs obligatoires');
      }
    } else if (step === 2) {
      if (validateStep2()) {
        setStep(3);
      } else {
        toast.error('Champs manquants', 'Veuillez remplir tous les champs obligatoires');
      }
    }
  };

  const handleSubmit = async () => {
    // Validation finale avant soumission
    if (!validateAll()) {
      toast.error('Validation échouée', 'Veuillez corriger les erreurs avant de soumettre');
      return;
    }

    setLoading(true);
    try {
      // Mapper les types du wizard vers les types backend
      const typeMapping = {
        'percent_item': 'percent_product',
        'percent_category': 'percent_category',
        'fixed_item': 'fixed_product',
        'fixed_category': 'fixed_category',
        'bogo': 'bogo',
        'conditional_discount': 'conditional_discount',
        'threshold': 'threshold',
        'shipping_free': 'shipping_free',
        'new_customer': 'new_customer',
        'happy_hour': 'happy_hour',
        'flash': 'flash',
        'seasonal': 'seasonal',
        'promo_code': 'promo_code'
      };

      // Déterminer applies_to en fonction des sélections
      // IMPORTANT: Ne PAS mettre 'all' par défaut si aucun produit n'est sélectionné
      let applies_to = 'specific_products'; // Par défaut, promo sur produits spécifiques
      if (formData.all_products) {
        applies_to = 'all';
      } else if (formData.eligible_categories.length > 0) {
        applies_to = 'specific_categories';
      } else if (formData.eligible_products.length > 0) {
        applies_to = 'specific_products';
      }
      // Si rien n'est sélectionné, on garde 'specific_products' avec une liste vide
      // Cela évite d'appliquer la promo à TOUS les produits par erreur

      // Mapper les données du formulaire vers le format backend
      const dataToSave = {
        name: formData.name,
        description: formData.description,
        type: typeMapping[formData.type] || formData.type,
        discount_value: formData.discount_value,
        // Mapper les champs de ciblage
        applies_to: applies_to,
        product_ids: formData.eligible_products || [],
        category_ids: formData.eligible_categories || [],
        // Dates
        start_date: formData.start_date,
        end_date: formData.end_date,
        // Horaires pour happy_hour/flash
        happy_hour_start: formData.start_time,
        happy_hour_end: formData.end_time,
        happy_hour_days: formData.days_active?.map(day => {
          const dayMap = { 'mon': 0, 'tue': 1, 'wed': 2, 'thu': 3, 'fri': 4, 'sat': 5, 'sun': 6 };
          return dayMap[day];
        }).filter(d => d !== undefined) || [],
        // BOGO
        buy_quantity: formData.bogo_buy_quantity,
        get_quantity: formData.bogo_get_quantity,
        max_per_order: formData.bogo_max_per_order,
        bogo_limit_type: formData.bogo_limit_type || 'per_order',
        // Limites — convention canonique : max_uses / max_uses_per_customer.
        // On écrit aussi usage_limit / per_customer_limit (miroir) pour ne pas
        // casser les écrans existants qui lisent encore ces anciens champs.
        max_uses: formData.limit_total,
        max_uses_per_customer: formData.limit_per_customer,
        usage_limit: formData.limit_total,
        per_customer_limit: formData.limit_per_customer,
        min_order_amount: formData.min_cart_amount,
        // Code promo - IMPORTANT: requires_code doit être TRUE si type = promo_code
        code: formData.promo_code,
        requires_code: formData.type === 'promo_code' ? true : formData.code_required,
        // Build 318: réservé à la première commande (nouveaux clients)
        first_order_only: formData.first_order_only || false,
        // Affichage
        badge_text: formData.badge_text,
        badge_color: formData.badge_color,
        is_visible_in_app: formData.is_visible_in_app,
        // Statut
        is_active: formData.status === 'active',
      };

      // FIREBASE DIRECT
      if (promotion) {
        await promotionsService.update(promotion.id, dataToSave);
      } else {
        await promotionsService.create(dataToSave);
      }

      toast.success('Promotion enregistrée', promotion ? 'Modification sauvegardée' : 'Nouvelle promotion créée');
      onSuccess?.();
    } catch (error) {
      console.error('Error saving promotion:', error);
      toast.error('Erreur', 'Impossible d\'enregistrer la promotion');
    } finally {
      setLoading(false);
    }
  };

  // Types de promos activés (les autres sont masqués mais pas supprimés)
  const ENABLED_PROMO_TYPES = ['bogo', 'percent_item', 'promo_code'];

  const getPromoTypeOptions = () => [
    { value: 'bogo', label: '🎁 BOGO (Achetez 1 = 1 offert)', description: 'Classique Buy One Get One', tooltip: 'Le client achète un produit et en reçoit un second identique gratuitement. Idéal pour augmenter le volume des ventes.' },
    { value: 'percent_item', label: '💯 % sur produit', description: 'Réduction % sur produits spécifiques', tooltip: 'Réduction en pourcentage appliquée sur des produits sélectionnés. Ex: -15% sur tous les burgers.' },
    { value: 'percent_category', label: '💯 % sur catégorie', description: 'Réduction % sur une catégorie', tooltip: 'Réduction en pourcentage sur tous les produits d\'une ou plusieurs catégories. Ex: -20% sur toutes les boissons.' },
    { value: 'conditional_discount', label: '🔢 2e à -50%', description: '2ème article à -50%, 3 pour 2...', tooltip: 'Réduction progressive: le 2ème article à -50%, ou formules "3 pour le prix de 2". Encourage l\'achat multiple.' },
    { value: 'threshold', label: '🎯 Seuil de panier', description: 'Dès X€ d\'achat', tooltip: 'Réduction activée quand le montant du panier atteint un seuil. Ex: -5€ dès 30€ d\'achat.' },
    { value: 'shipping_free', label: '🚚 Livraison gratuite', description: 'Frais de livraison offerts', tooltip: 'Les frais de livraison sont offerts. Peut être conditionné à un montant minimum.' },
    { value: 'new_customer', label: '✨ Nouveau client', description: '1ère commande uniquement', tooltip: 'Offre réservée aux nouveaux clients pour leur première commande. Excellent pour l\'acquisition.' },
    { value: 'happy_hour', label: '🌅 Happy Hour', description: 'Promo sur horaires définis', tooltip: 'Réduction active uniquement pendant certaines heures de la journée. Ex: -15% de 15h à 18h.' },
    { value: 'flash', label: '⚡ Offre Flash', description: 'Durée très limitée', tooltip: 'Promotion de très courte durée (quelques heures) pour créer un sentiment d\'urgence.' },
    { value: 'seasonal', label: '🎉 Saisonnier', description: 'Événement spécial', tooltip: 'Promotion liée à un événement (Noël, St-Valentin, etc.) ou une saison spécifique.' },
    { value: 'promo_code', label: '🔖 Code promo', description: 'Code manuel à saisir', tooltip: 'Le client doit saisir un code pour bénéficier de la réduction. Utile pour le marketing ciblé.' }
  ].filter(opt => ENABLED_PROMO_TYPES.includes(opt.value));

  const renderStep1 = () => {
    // Types avec configuration spécifique dans step 2, donc pas de champs remise génériques en step 1
    const typesWithSpecificConfig = ['bogo', 'shipping_free', 'new_customer', 'seasonal'];
    const shouldShowDiscountFields = !typesWithSpecificConfig.includes(formData.type);
    const needsProductSelection = ['percent_item', 'fixed_item', 'bogo', 'conditional_discount'].includes(formData.type);
    const needsCategorySelection = ['percent_category', 'fixed_category'].includes(formData.type);
    
    const filteredProducts = products.filter(p => 
      p.name.toLowerCase().includes(productSearch.toLowerCase())
    );
    
    return (
      <div className="space-y-4">
        <h3 className="text-lg font-bold mb-4">Étape 1: Type & Ciblage</h3>
        
        {/* Type de promo avec tooltips */}
        <div>
          <label className="block text-sm font-medium mb-2">Type de promotion *</label>
          <div className="grid grid-cols-2 gap-2">
            {getPromoTypeOptions().map(opt => (
              <div
                key={opt.value}
                className={`relative group cursor-pointer p-3 border-2 rounded-lg transition-all ${
                  formData.type === opt.value 
                    ? 'border-primary bg-primary/5' 
                    : 'border-gray-200 hover:border-primary/50'
                }`}
                onClick={() => setFormData({...formData, type: opt.value})}
              >
                <div className="font-medium text-sm">{opt.label}</div>
                <div className="text-xs text-gray-500">{opt.description}</div>
                
                {/* Tooltip */}
                <div className="absolute left-0 top-full mt-2 w-64 p-3 bg-gray-900 text-white text-xs rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50">
                  <div className="font-semibold mb-1">ℹ️ Comment ça marche?</div>
                  {opt.tooltip}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Nom */}
        <div>
          <label className="block text-sm font-medium mb-2">Nom de la promo *</label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => setFormData({...formData, name: e.target.value})}
            className={`w-full px-3 py-2 border rounded-lg ${errors.name ? 'border-red-500 bg-red-50' : ''}`}
            placeholder="Ex: Happy Hour Burgers"
            required
          />
          {errors.name && <p className="text-red-500 text-xs mt-1">⚠️ {errors.name}</p>}
        </div>

        {/* Description */}
        <div>
          <label className="block text-sm font-medium mb-2">Description</label>
          <textarea
            value={formData.description}
            onChange={(e) => setFormData({...formData, description: e.target.value})}
            className="w-full px-3 py-2 border rounded-lg"
            rows="2"
            placeholder="Profitez de -15% sur tous nos burgers entre 15h et 18h"
          />
        </div>

        {/* Valeur remise - Conditionnel */}
        {shouldShowDiscountFields && (
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Type de remise</label>
              <select
                value={formData.discount_type}
                onChange={(e) => setFormData({...formData, discount_type: e.target.value})}
                className="w-full px-3 py-2 border rounded-lg"
              >
                <option value="percentage">Pourcentage (%)</option>
                <option value="fixed">Montant fixe (€)</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Valeur *</label>
              <input
                type="number"
                step="0.01"
                min="0.01"
                max={formData.discount_type === 'percentage' ? 100 : undefined}
                value={formData.discount_value}
                onChange={(e) => setFormData({...formData, discount_value: parseFloat(e.target.value)})}
                className={`w-full px-3 py-2 border rounded-lg ${errors.discount_value ? 'border-red-500 bg-red-50' : ''}`}
                required
              />
              {errors.discount_value && <p className="text-red-500 text-xs mt-1">⚠️ {errors.discount_value}</p>}
            </div>
          </div>
        )}

        {/* BOGO Configuration */}
        {formData.type === 'bogo' && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 space-y-3">
            <h4 className="font-semibold text-green-800">🎁 Configuration BOGO</h4>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Acheter (quantité)</label>
                <input
                  type="number"
                  min="1"
                  value={formData.bogo_buy_quantity || 1}
                  onChange={(e) => setFormData({...formData, bogo_buy_quantity: parseInt(e.target.value)})}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Obtenir gratuit (quantité)</label>
                <input
                  type="number"
                  min="1"
                  value={formData.bogo_get_quantity || 1}
                  onChange={(e) => setFormData({...formData, bogo_get_quantity: parseInt(e.target.value)})}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
            </div>
            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={formData.bogo_cheapest_free || false}
                onChange={(e) => setFormData({...formData, bogo_cheapest_free: e.target.checked})}
                className="w-4 h-4"
              />
              <span className="text-sm">Le moins cher est gratuit (si produits différents)</span>
            </label>

            {/* Limite BOGO */}
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mt-2">
              <label className="block text-sm font-medium mb-2">
                🚫 Limite des offerts
              </label>

              {/* Type de limite */}
              <div className="flex gap-4 mb-3">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="bogo_limit_type"
                    value="per_order"
                    checked={formData.bogo_limit_type === 'per_order'}
                    onChange={(e) => setFormData({...formData, bogo_limit_type: e.target.value})}
                    className="w-4 h-4"
                  />
                  <span className="text-sm">Par commande</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="bogo_limit_type"
                    value="per_day"
                    checked={formData.bogo_limit_type === 'per_day'}
                    onChange={(e) => setFormData({...formData, bogo_limit_type: e.target.value})}
                    className="w-4 h-4"
                  />
                  <span className="text-sm">Par jour (par client)</span>
                </label>
              </div>

              {/* Nombre max */}
              <div className="flex items-center gap-3">
                <input
                  type="number"
                  min="1"
                  placeholder="Illimité"
                  value={formData.bogo_max_per_order || ''}
                  onChange={(e) => setFormData({...formData, bogo_max_per_order: e.target.value ? parseInt(e.target.value) : null})}
                  className="w-24 px-3 py-2 border rounded-lg"
                />
                <span className="text-sm text-gray-600">
                  {formData.bogo_max_per_order
                    ? `Max ${formData.bogo_max_per_order} offert(s) ${formData.bogo_limit_type === 'per_day' ? 'par jour' : 'par commande'}`
                    : 'Pas de limite (illimité)'}
                </span>
              </div>
              <p className="text-xs text-yellow-700 mt-1">
                {formData.bogo_limit_type === 'per_day'
                  ? 'Le client ne pourra recevoir que ce nombre d\'offerts par jour, même sur plusieurs commandes.'
                  : 'Le client ne pourra recevoir que ce nombre d\'offerts par commande.'}
              </p>
            </div>

            <p className="text-xs text-green-600">
              Exemple: Acheter {formData.bogo_buy_quantity || 1} = {formData.bogo_get_quantity || 1} offert(s)
              {formData.bogo_max_per_order && ` (max ${formData.bogo_max_per_order} ${formData.bogo_limit_type === 'per_day' ? 'par jour' : 'par commande'})`}
            </p>
          </div>
        )}

        {/* Conditional Discount Configuration */}
        {formData.type === 'conditional_discount' && (
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 space-y-3">
            <h4 className="font-semibold text-purple-800">🔢 Configuration Conditionnelle</h4>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">À partir du Xème article</label>
                <input
                  type="number"
                  min="2"
                  value={formData.conditional_quantity || 2}
                  onChange={(e) => setFormData({...formData, conditional_quantity: parseInt(e.target.value)})}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Réduction (%)</label>
                <input
                  type="number"
                  min="1"
                  max="100"
                  value={formData.conditional_discount_percent || 50}
                  onChange={(e) => setFormData({...formData, conditional_discount_percent: parseInt(e.target.value)})}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
            </div>
            <p className="text-xs text-purple-600">
              Exemple: Le {formData.conditional_quantity || 2}ème article à -{formData.conditional_discount_percent || 50}%
            </p>
          </div>
        )}

        {/* Ciblage produits - Amélioré */}
        {needsProductSelection && (
          <div>
            <label className="block text-sm font-medium mb-2">Produits éligibles *</label>
            {errors.eligible_products && <p className="text-red-500 text-xs mb-2">⚠️ {errors.eligible_products}</p>}
            
            {/* Option toute la carte */}
            <div className="mb-3">
              <label className="flex items-center space-x-2 p-3 border-2 rounded-lg cursor-pointer hover:bg-gray-50">
                <input
                  type="checkbox"
                  checked={formData.all_products}
                  onChange={(e) => setFormData({
                    ...formData, 
                    all_products: e.target.checked,
                    eligible_products: e.target.checked ? [] : formData.eligible_products
                  })}
                  className="w-4 h-4"
                />
                <span className="font-medium">🍽️ Toute la carte</span>
                <span className="text-xs text-gray-500">(Tous les produits sont éligibles)</span>
              </label>
            </div>

            {!formData.all_products && (
              <>
                {/* Recherche */}
                <input
                  type="text"
                  placeholder="🔍 Rechercher un produit..."
                  value={productSearch}
                  onChange={(e) => setProductSearch(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg mb-2"
                />
                
                {/* Liste avec checkboxes */}
                <div className="border rounded-lg max-h-48 overflow-y-auto p-2 space-y-1">
                  {filteredProducts.length === 0 ? (
                    <p className="text-center text-gray-400 py-4">Aucun produit trouvé</p>
                  ) : (
                    filteredProducts.map(p => (
                      <label
                        key={p.id}
                        className="flex items-center space-x-2 p-2 hover:bg-gray-50 rounded cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={formData.eligible_products.includes(p.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setFormData({
                                ...formData,
                                eligible_products: [...formData.eligible_products, p.id]
                              });
                            } else {
                              setFormData({
                                ...formData,
                                eligible_products: formData.eligible_products.filter(id => id !== p.id)
                              });
                            }
                          }}
                          className="w-4 h-4"
                        />
                        <span className="flex-1">{p.name}</span>
                        <span className="text-sm text-gray-500">{p.price}€</span>
                      </label>
                    ))
                  )}
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  {formData.eligible_products.length} produit(s) sélectionné(s)
                </p>
              </>
            )}
          </div>
        )}

        {/* Ciblage catégories - Amélioré */}
        {needsCategorySelection && (
          <div>
            <label className="block text-sm font-medium mb-2">Catégories éligibles *</label>
            {errors.eligible_categories && <p className="text-red-500 text-xs mb-2">⚠️ {errors.eligible_categories}</p>}
            <p className="text-xs text-gray-500 mb-2">
              Le client pourra choisir n'importe quel produit de ces catégories
            </p>
            
            <div className="grid grid-cols-2 gap-2">
              {categories.map(c => (
                <label
                  key={c.id}
                  className="flex items-center space-x-2 p-3 border-2 rounded-lg cursor-pointer hover:bg-gray-50"
                >
                  <input
                    type="checkbox"
                    checked={formData.eligible_categories.includes(c.id)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setFormData({
                          ...formData,
                          eligible_categories: [...formData.eligible_categories, c.id]
                        });
                      } else {
                        setFormData({
                          ...formData,
                          eligible_categories: formData.eligible_categories.filter(id => id !== c.id)
                        });
                      }
                    }}
                    className="w-4 h-4"
                  />
                  <span className="font-medium">{c.name}</span>
                </label>
              ))}
            </div>
            <p className="text-xs text-gray-500 mt-1">
              {formData.eligible_categories.length} catégorie(s) sélectionnée(s)
            </p>
          </div>
        )}
      </div>
    );
  };

  const renderStep2 = () => (
    <div className="space-y-4">
      <h3 className="text-lg font-bold mb-4">Étape 2: Conditions & Limites</h3>
      
      {/* Dates */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium mb-2">Date début *</label>
          <input
            type="date"
            value={formData.start_date}
            onChange={(e) => setFormData({...formData, start_date: e.target.value})}
            className={`w-full px-3 py-2 border rounded-lg ${errors.start_date ? 'border-red-500 bg-red-50' : ''}`}
            required
          />
          {errors.start_date && <p className="text-red-500 text-xs mt-1">⚠️ {errors.start_date}</p>}
        </div>
        <div>
          <label className="block text-sm font-medium mb-2">Date fin *</label>
          <input
            type="date"
            value={formData.end_date}
            onChange={(e) => setFormData({...formData, end_date: e.target.value})}
            className={`w-full px-3 py-2 border rounded-lg ${errors.end_date ? 'border-red-500 bg-red-50' : ''}`}
            required
          />
          {errors.end_date && <p className="text-red-500 text-xs mt-1">⚠️ {errors.end_date}</p>}
        </div>
      </div>

      {/* Horaires - pour happy_hour et flash */}
      {['happy_hour', 'flash'].includes(formData.type) && (
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-2">Heure début {formData.type === 'happy_hour' ? '*' : ''}</label>
            <input
              type="time"
              value={formData.start_time || ''}
              onChange={(e) => setFormData({...formData, start_time: e.target.value})}
              className={`w-full px-3 py-2 border rounded-lg ${errors.start_time ? 'border-red-500 bg-red-50' : ''}`}
            />
            {errors.start_time && <p className="text-red-500 text-xs mt-1">⚠️ {errors.start_time}</p>}
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Heure fin {formData.type === 'happy_hour' ? '*' : ''}</label>
            <input
              type="time"
              value={formData.end_time || ''}
              onChange={(e) => setFormData({...formData, end_time: e.target.value})}
              className={`w-full px-3 py-2 border rounded-lg ${errors.end_time ? 'border-red-500 bg-red-50' : ''}`}
            />
            {errors.end_time && <p className="text-red-500 text-xs mt-1">⚠️ {errors.end_time}</p>}
          </div>
        </div>
      )}

      {/* Nouveau client */}
      {formData.type === 'new_customer' && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-3">
          <h4 className="font-semibold text-blue-800">✨ Configuration Nouveau Client</h4>
          <p className="text-sm text-blue-600">
            Cette promotion sera automatiquement appliquée uniquement pour la première commande de chaque client.
          </p>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Type de remise</label>
              <select
                value={formData.discount_type}
                onChange={(e) => setFormData({...formData, discount_type: e.target.value})}
                className="w-full px-3 py-2 border rounded-lg"
              >
                <option value="percentage">Pourcentage (%)</option>
                <option value="fixed">Montant fixe (€)</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Valeur de la remise</label>
              <input
                type="number"
                step="0.01"
                value={formData.discount_value}
                onChange={(e) => setFormData({...formData, discount_value: parseFloat(e.target.value)})}
                className="w-full px-3 py-2 border rounded-lg"
              />
            </div>
          </div>
        </div>
      )}

      {/* Livraison gratuite */}
      {formData.type === 'shipping_free' && (
        <div className="bg-teal-50 border border-teal-200 rounded-lg p-4 space-y-3">
          <h4 className="font-semibold text-teal-800">🚚 Configuration Livraison Gratuite</h4>
          <div>
            <label className="block text-sm font-medium mb-1">Montant minimum panier (optionnel)</label>
            <input
              type="number"
              step="0.01"
              value={formData.min_cart_amount || ''}
              onChange={(e) => setFormData({...formData, min_cart_amount: parseFloat(e.target.value) || null})}
              className="w-full px-3 py-2 border rounded-lg"
              placeholder="Laisser vide pour aucun minimum"
            />
            <p className="text-xs text-teal-600 mt-1">
              {formData.min_cart_amount
                ? `Livraison gratuite dès ${formData.min_cart_amount}€ d'achat`
                : 'Livraison gratuite sans condition de montant'}
            </p>
          </div>
        </div>
      )}

      {/* Saisonnier */}
      {formData.type === 'seasonal' && (
        <div className="bg-pink-50 border border-pink-200 rounded-lg p-4 space-y-3">
          <h4 className="font-semibold text-pink-800">🎉 Configuration Saisonnière</h4>
          <p className="text-sm text-pink-600 mb-2">
            Idéal pour les événements spéciaux (Noël, Saint-Valentin, etc.)
          </p>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Type de remise</label>
              <select
                value={formData.discount_type}
                onChange={(e) => setFormData({...formData, discount_type: e.target.value})}
                className="w-full px-3 py-2 border rounded-lg"
              >
                <option value="percentage">Pourcentage (%)</option>
                <option value="fixed">Montant fixe (€)</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Valeur de la remise</label>
              <input
                type="number"
                step="0.01"
                value={formData.discount_value}
                onChange={(e) => setFormData({...formData, discount_value: parseFloat(e.target.value)})}
                className="w-full px-3 py-2 border rounded-lg"
              />
            </div>
          </div>
        </div>
      )}

      {/* Jours actifs */}
      <div>
        <label className="block text-sm font-medium mb-2">Jours actifs</label>
        <div className="flex flex-wrap gap-2">
          {['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'].map((day, i) => {
            const dayCode = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'][i];
            const isActive = formData.days_active.includes(dayCode);
            return (
              <button
                key={dayCode}
                type="button"
                onClick={() => {
                  if (isActive) {
                    setFormData({
                      ...formData,
                      days_active: formData.days_active.filter(d => d !== dayCode)
                    });
                  } else {
                    setFormData({
                      ...formData,
                      days_active: [...formData.days_active, dayCode]
                    });
                  }
                }}
                className={`px-3 py-1 rounded text-sm font-medium ${
                  isActive ? 'bg-primary text-white' : 'bg-gray-200'
                }`}
              >
                {day}
              </button>
            );
          })}
        </div>
      </div>

      {/* Montant minimum */}
      {formData.type === 'threshold' && (
        <div>
          <label className="block text-sm font-medium mb-2">Montant minimum panier (€) *</label>
          <input
            type="number"
            step="0.01"
            min="0.01"
            value={formData.min_cart_amount || ''}
            onChange={(e) => setFormData({...formData, min_cart_amount: parseFloat(e.target.value)})}
            className={`w-full px-3 py-2 border rounded-lg ${errors.min_cart_amount ? 'border-red-500 bg-red-50' : ''}`}
          />
          {errors.min_cart_amount && <p className="text-red-500 text-xs mt-1">⚠️ {errors.min_cart_amount}</p>}
        </div>
      )}

      {/* Code promo */}
      {formData.type === 'promo_code' && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 space-y-3">
          <h4 className="font-semibold text-yellow-800">🔖 Configuration Code Promo</h4>
          <div>
            <label className="block text-sm font-medium mb-1">Code à saisir *</label>
            <input
              type="text"
              value={formData.promo_code || ''}
              onChange={(e) => setFormData({...formData, promo_code: e.target.value.toUpperCase().replace(/\s/g, '')})}
              className={`w-full px-3 py-2 border rounded-lg uppercase ${errors.promo_code ? 'border-red-500 bg-red-50' : ''}`}
              placeholder="Ex: BIENVENUE20"
            />
            {errors.promo_code && <p className="text-red-500 text-xs mt-1">⚠️ {errors.promo_code}</p>}
          </div>
          <label className="flex items-center space-x-2">
            <input
              type="checkbox"
              checked={formData.code_required || false}
              onChange={(e) => setFormData({...formData, code_required: e.target.checked})}
              className="w-4 h-4"
            />
            <span className="text-sm">Code obligatoire (la promo ne s'applique pas automatiquement)</span>
          </label>
          <label className="flex items-center space-x-2">
            <input
              type="checkbox"
              checked={formData.first_order_only || false}
              onChange={(e) => setFormData({...formData, first_order_only: e.target.checked})}
              className="w-4 h-4"
            />
            <span className="text-sm">✨ Réservé à la première commande (nouveaux clients uniquement)</span>
          </label>
        </div>
      )}

      {/* Limites */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium mb-2">Limite par client</label>
          <input
            type="number"
            value={formData.limit_per_customer || ''}
            onChange={(e) => setFormData({...formData, limit_per_customer: parseInt(e.target.value) || null})}
            className="w-full px-3 py-2 border rounded-lg"
            placeholder="Illimité"
          />
          <p className="text-xs text-gray-500 mt-1">Nombre max d'utilisation par client</p>
        </div>
        <div>
          <label className="block text-sm font-medium mb-2">Limite totale</label>
          <input
            type="number"
            value={formData.limit_total || ''}
            onChange={(e) => setFormData({...formData, limit_total: parseInt(e.target.value) || null})}
            className="w-full px-3 py-2 border rounded-lg"
            placeholder="Illimité"
          />
          <p className="text-xs text-gray-500 mt-1">Nombre max d'utilisation total</p>
        </div>
      </div>

      {/* Priorité */}
      <div>
        <label className="block text-sm font-medium mb-2">Priorité (0-100)</label>
        <input
          type="number"
          value={formData.priority}
          onChange={(e) => setFormData({...formData, priority: parseInt(e.target.value) || 0})}
          className="w-full px-3 py-2 border rounded-lg"
        />
        <p className="text-xs text-gray-500 mt-1">Plus la priorité est élevée, plus la promo est appliquée en premier</p>
      </div>

      {/* Cumulable */}
      <div className="flex items-center gap-2">
        <input
          type="checkbox"
          id="stackable"
          checked={formData.stackable}
          onChange={(e) => setFormData({...formData, stackable: e.target.checked})}
          className="w-4 h-4"
        />
        <label htmlFor="stackable" className="text-sm font-medium">
          Cumulable avec d'autres promotions
        </label>
      </div>
    </div>
  );

  const renderStep3 = () => {
    // Récupérer les noms des produits/catégories sélectionnés
    const selectedProductNames = formData.eligible_products
      .map(id => products.find(p => p.id === id)?.name)
      .filter(Boolean);

    const selectedCategoryNames = formData.eligible_categories
      .map(id => categories.find(c => c.id === id)?.name)
      .filter(Boolean);

    // Types qui n'ont PAS de badge (promos panier ou code)
    const typesWithoutBadge = ['promo_code', 'threshold', 'shipping_free', 'new_customer'];
    const showBadgeFields = !typesWithoutBadge.includes(formData.type);

    return (
      <div className="space-y-4">
        <h3 className="text-lg font-bold mb-4">Étape 3: Affichage & Activation</h3>

        {/* Badge - Seulement pour les promos avec badge sur produit */}
        {showBadgeFields ? (
          <>
            <div>
              <label className="block text-sm font-medium mb-2">Texte badge (affiché sur produits)</label>
              <input
                type="text"
                value={formData.badge_text}
                onChange={(e) => setFormData({...formData, badge_text: e.target.value})}
                className="w-full px-3 py-2 border rounded-lg"
                placeholder="Ex: -15% 🔥"
              />
            </div>

            {/* Couleur badge */}
            <div>
              <label className="block text-sm font-medium mb-2">Couleur du badge</label>
              <input
                type="color"
                value={formData.badge_color}
                onChange={(e) => setFormData({...formData, badge_color: e.target.value})}
                className="w-20 h-10 border rounded-lg"
              />
            </div>
          </>
        ) : (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <p className="text-sm text-gray-600">
              ℹ️ Ce type de promotion n'affiche pas de badge sur les produits.
              {formData.type === 'promo_code' && ' Le client doit saisir un code pour bénéficier de la réduction.'}
              {formData.type === 'threshold' && ' La réduction s\'applique automatiquement au panier.'}
              {formData.type === 'shipping_free' && ' La livraison gratuite s\'applique automatiquement.'}
              {formData.type === 'new_customer' && ' La réduction s\'applique automatiquement pour les nouveaux clients.'}
            </p>
          </div>
        )}

        {/* Statut */}
        <div>
          <label className="block text-sm font-medium mb-2">Statut</label>
          <select
            value={formData.status}
            onChange={(e) => setFormData({...formData, status: e.target.value})}
            className="w-full px-3 py-2 border rounded-lg"
          >
            <option value="draft">🟡 Brouillon</option>
            <option value="active">🟢 Active</option>
            <option value="paused">🟠 Pause</option>
          </select>
        </div>

        {/* Visibilité dans l'app */}
        <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg">
          <input
            type="checkbox"
            id="is_visible_in_app"
            checked={formData.is_visible_in_app}
            onChange={(e) => setFormData({...formData, is_visible_in_app: e.target.checked})}
            className="w-5 h-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          <div>
            <label htmlFor="is_visible_in_app" className="text-sm font-medium cursor-pointer">
              📱 Visible dans l'app mobile
            </label>
            <p className="text-xs text-gray-500">
              Afficher cette promotion aux clients dans l'application
            </p>
          </div>
        </div>

        {/* Aperçu amélioré */}
        <div className="p-4 border-2 border-dashed rounded-lg bg-gradient-to-br from-blue-50 to-purple-50">
          <h4 className="font-bold mb-3 text-lg">📋 Aperçu de la promotion</h4>
          <div className="space-y-2 text-sm">
            <p><strong>Nom:</strong> {formData.name || '(Non défini)'}</p>
            <p><strong>Type:</strong> {getPromoTypeOptions().find(o => o.value === formData.type)?.label}</p>
            
            {!['bogo', 'shipping_free'].includes(formData.type) && (
              <p><strong>Remise:</strong> {formData.discount_value}{formData.discount_type === 'percentage' ? '%' : '€'}</p>
            )}
            
            <p><strong>Période:</strong> {formData.start_date} → {formData.end_date}</p>
            
            {formData.start_time && formData.end_time && (
              <p><strong>Horaires:</strong> {formData.start_time} - {formData.end_time}</p>
            )}
            
            {/* Produits éligibles */}
            {formData.all_products && (
              <p><strong>Produits:</strong> <span className="text-green-600 font-semibold">🍽️ Toute la carte</span></p>
            )}
            
            {!formData.all_products && selectedProductNames.length > 0 && (
              <div>
                <strong>Produits éligibles:</strong>
                <div className="mt-1 flex flex-wrap gap-1">
                  {selectedProductNames.map((name, i) => (
                    <span key={i} className="inline-block px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs font-medium">
                      {name}
                    </span>
                  ))}
                </div>
              </div>
            )}
            
            {/* Catégories éligibles */}
            {selectedCategoryNames.length > 0 && (
              <div>
                <strong>Catégories éligibles:</strong>
                <div className="mt-1 flex flex-wrap gap-1">
                  {selectedCategoryNames.map((name, i) => (
                    <span key={i} className="inline-block px-2 py-1 bg-purple-100 text-purple-700 rounded text-xs font-medium">
                      📂 {name}
                    </span>
                  ))}
                </div>
              </div>
            )}
            
            {/* Badge - seulement pour les types avec badge */}
            {showBadgeFields && formData.badge_text && (
              <div className="mt-3">
                <strong>Badge:</strong>
                <div className="inline-block px-3 py-1 rounded font-bold text-white ml-2" style={{backgroundColor: formData.badge_color}}>
                  {formData.badge_text}
                </div>
              </div>
            )}

            {/* Code promo - seulement pour type promo_code */}
            {formData.type === 'promo_code' && formData.promo_code && (
              <div className="mt-3">
                <strong>Code promo:</strong>
                <span className="inline-block px-3 py-1 bg-yellow-100 text-yellow-800 rounded font-mono font-bold ml-2">
                  {formData.promo_code}
                </span>
              </div>
            )}

            {/* Seuil minimum - pour threshold et shipping_free */}
            {['threshold', 'shipping_free'].includes(formData.type) && formData.min_cart_amount && (
              <p><strong>Seuil minimum:</strong> {formData.min_cart_amount}€</p>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <Modal 
      isOpen={isOpen} 
      onClose={onClose} 
      title={promotion ? '✏️ Modifier la promotion' : '➕ Créer une promotion'}
      size="lg"
    >
      <div className="p-2">

        {/* Progress Steps */}
        <div className="flex items-center justify-between mb-8">
          {[1, 2, 3].map(s => (
            <div key={s} className="flex items-center">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
                step >= s ? 'bg-primary text-white' : 'bg-gray-200'
              }`}>
                {step > s ? <Check className="w-6 h-6" /> : s}
              </div>
              {s < 3 && <div className={`w-20 h-1 mx-2 ${step > s ? 'bg-primary' : 'bg-gray-200'}`} />}
            </div>
          ))}
        </div>

        {/* Step Content */}
        <form onSubmit={(e) => e.preventDefault()}>
          {step === 1 && renderStep1()}
          {step === 2 && renderStep2()}
          {step === 3 && renderStep3()}

          {/* Navigation Buttons */}
          <div className="flex justify-between mt-8 pt-4 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setErrors({});
                step === 1 ? onClose() : setStep(step - 1);
              }}
            >
              <ChevronLeft className="w-4 h-4 mr-2" />
              {step === 1 ? 'Annuler' : 'Précédent'}
            </Button>

            {step < 3 ? (
              <Button
                type="button"
                onClick={handleNextStep}
              >
                Suivant
                <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            ) : (
              <Button
                type="button"
                onClick={handleSubmit}
                disabled={loading}
              >
                {loading ? 'Enregistrement...' : '✅ Enregistrer'}
              </Button>
            )}
          </div>
        </form>
      </div>
    </Modal>
  );
};
